import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, FileText, CreditCard, Building2, ChevronDown, ChevronRight } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  due: { label: "Due", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  invoiced: { label: "Facturée", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  paid: { label: "Payée", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function AdminCommissions() {
  const [data, setData] = useState({ schools: [], totals: {}, billing_day: 15 });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [invoicing, setInvoicing] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/booking/commissions")
      .then(({ data }) => setData(data || { schools: [], totals: {}, billing_day: 15 }))
      .catch(() => toast.error("Impossible de charger les commissions."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const invoice = async (schoolId) => {
    if (!window.confirm("Générer le relevé mensuel et le lien de paiement Stripe pour cette auto-école ?")) return;
    setInvoicing(schoolId);
    try {
      const { data } = await api.post(`/admin/booking/commissions/school/${schoolId}/invoice`, {
        origin_url: typeof window !== "undefined" ? window.location.origin : "",
      });
      if (data?.checkout_url) {
        window.open(data.checkout_url, "_blank");
        toast.success("Lien de paiement généré.");
      } else {
        toast.success("Relevé généré.");
      }
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Clé Stripe invalide ou aucune commission due."); }
    finally { setInvoicing(""); }
  };

  const money = (n) => `${Number(n || 0).toFixed(2)} €`;

  return (
    <Shell role="admin">
      <div className="mb-5">
        <h1 className="text-2xl font-black tracking-tight text-neutral-900">Commissions auto-écoles</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Commission due sur chaque inscription finalisée (% du forfait). Paiement le {data.billing_day || 15} de chaque mois.
        </p>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-neutral-400">Total dû</p>
          <p className="mt-1 text-2xl font-black text-amber-600" data-testid="comm-total-due">{money(data.totals?.due_total)}</p>
          <p className="text-xs text-neutral-400">{data.totals?.due_count || 0} dossier(s)</p>
        </div>
        <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-neutral-400">Total encaissé</p>
          <p className="mt-1 text-2xl font-black text-emerald-600" data-testid="comm-total-paid">{money(data.totals?.paid_total)}</p>
        </div>
        <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-neutral-400">Auto-écoles</p>
          <p className="mt-1 text-2xl font-black text-neutral-900">{(data.schools || []).length}</p>
        </div>
      </div>

      <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : (data.schools || []).length === 0 ? (
          <p className="p-8 text-center text-sm text-neutral-500" data-testid="comm-empty">{"Aucune commission pour le moment. Elles apparaissent dès qu'une auto-école finalise une inscription."}</p>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {data.schools.map((s) => {
              const isOpen = !!open[s.school_id];
              return (
                <li key={s.school_id} data-testid={`comm-school-${s.school_id}`}>
                  <div className="flex items-center justify-between gap-3 px-4 py-3">
                    <button onClick={() => setOpen((o) => ({ ...o, [s.school_id]: !o[s.school_id] }))} className="flex min-w-0 items-center gap-2 text-left">
                      {isOpen ? <ChevronDown className="h-4 w-4 text-neutral-400" /> : <ChevronRight className="h-4 w-4 text-neutral-400" />}
                      <Building2 className="h-4 w-4 shrink-0 text-brand" />
                      <span className="min-w-0">
                        <span className="block truncate font-bold text-neutral-900">{s.school_name || "—"}</span>
                        <span className="text-xs text-neutral-500">{s.due_count} due(s) · {money(s.due_total)} · {money(s.paid_total)} payé</span>
                      </span>
                    </button>
                    <div className="flex shrink-0 items-center gap-2">
                      <a href={`${BACKEND}/api/admin/booking/commissions/school/${s.school_id}/statement.pdf`} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded-lg bg-neutral-100 px-2.5 py-1.5 text-xs font-bold text-neutral-700 hover:bg-neutral-200"><FileText className="h-3.5 w-3.5" /> Relevé</a>
                      <button disabled={invoicing === s.school_id || !s.due_total} onClick={() => invoice(s.school_id)} data-testid={`comm-invoice-${s.school_id}`}
                        className="inline-flex items-center gap-1 rounded-lg bg-brand px-2.5 py-1.5 text-xs font-bold text-white hover:bg-brand-hover disabled:opacity-40">
                        {invoicing === s.school_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CreditCard className="h-3.5 w-3.5" />} Encaisser
                      </button>
                    </div>
                  </div>
                  {isOpen && (
                    <div className="bg-neutral-50 px-4 py-2">
                      <table className="w-full text-left text-xs">
                        <thead className="text-neutral-400">
                          <tr><th className="py-1">Élève</th><th>Ville / Mois</th><th className="text-right">Forfait</th><th className="text-right">Taux</th><th className="text-right">Commission</th><th className="text-right">Statut</th></tr>
                        </thead>
                        <tbody>
                          {(s.items || []).map((c) => {
                            const meta = STATUS_META[c.status] || { label: c.status, cls: "bg-neutral-100" };
                            return (
                              <tr key={c.id} className="border-t border-neutral-200/70">
                                <td className="py-1.5 font-semibold text-neutral-800">{c.student_name || "—"}</td>
                                <td className="text-neutral-600">{c.city} · {c.month_label}</td>
                                <td className="text-right text-neutral-600">{money(c.forfait_price)}</td>
                                <td className="text-right text-neutral-600">{Math.round((c.rate || 0) * 100)}%</td>
                                <td className="text-right font-bold text-neutral-900">{money(c.amount)}</td>
                                <td className="text-right"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${meta.cls}`}>{meta.label}</span></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Shell>
  );
}
